/**
 * PBI 1962 (AB#1968) — Service option-groups domain types (web).
 *
 * These mirror the Phase 2 backend contract (backend-handoff-phase2.md):
 *   ServiceOptionGroupDto { id, name, nameAr?, selectionType, kind, minSelect,
 *     maxSelect, required(=minSelect>=1), sortOrder, options[] }
 *   ServiceOptionItemDto  { id, name, nameAr?, price(decimal), currency,
 *     duration, sortOrder }
 *
 * Enums serialize as the enum NAME on the wire (AB#1894 convention):
 *   selectionType = "Single" | "Multiple"
 *   kind          = "Variant" | "AddOn"
 *
 * HYBRID pricing semantics (Faris §1.3):
 *   - Variant option → Price/Duration are ABSOLUTE (replace base).
 *   - AddOn option   → Price/Duration are ADDITIVE deltas (+JOD/AED / +minutes).
 */

export type OptionSelectionType = 'Single' | 'Multiple';
export type OptionGroupKind = 'Variant' | 'AddOn';

/** Wire shape returned by the backend read DTOs (GET service / salon detail). */
export interface ServiceOptionItemDto {
    id: number;
    name: string;
    nameAr?: string | null;
    /** decimal(18,2). Absolute for a Variant option, +delta for an AddOn option. */
    price: number;
    currency: string;
    /** "HH:mm:ss" TimeSpan string. Absolute for Variant, +delta for AddOn. */
    duration: string;
    sortOrder: number;
}

export interface ServiceOptionGroupDto {
    id: number;
    name: string;
    nameAr?: string | null;
    selectionType: OptionSelectionType;
    kind: OptionGroupKind;
    minSelect: number;
    maxSelect: number;
    /** Derived server-side as minSelect >= 1. Advisory on the client. */
    required: boolean;
    sortOrder: number;
    /**
     * M54 legacy auto-wrap marker. The backend backfilled each legacy service's
     * flat pricing options into ONE implicit Single/1..1 Variant "Options" group.
     * When present and true, the builder renders a read-only "Auto-wrapped
     * (legacy)" badge; the group is still fully editable into real groups.
     * (Optional — older/newer DTO revisions may omit it; treat absent as false.)
     */
    isImplicit?: boolean;
    options: ServiceOptionItemDto[];
}

/**
 * Local editor model. Uses stable client-side ids for React keys and
 * reorder/remove operations. Prices are held per-currency-agnostic single
 * `price` number (single currency per service — see the schema). `durationMin`
 * is the human-facing minutes value; it is converted to "HH:mm:ss" only at the
 * serialize boundary.
 */
export interface OptionRowModel {
    /** Stable client key (not the server id). */
    key: string;
    /** Server id when editing an existing option; undefined for new rows. */
    id?: number;
    name: string;
    nameAr: string;
    /** Absolute (Variant) or +delta (AddOn) price in the service currency. */
    price: number;
    /** Duration in whole minutes. Absolute (Variant) or +delta (AddOn). */
    durationMin: number;
}

export interface OptionGroupModel {
    key: string;
    id?: number;
    name: string;
    nameAr: string;
    kind: OptionGroupKind;
    selectionType: OptionSelectionType;
    minSelect: number;
    maxSelect: number;
    /** Read-only legacy auto-wrap badge (from M54 backfill). */
    isImplicit: boolean;
    options: OptionRowModel[];
}

/**
 * Serialized wire shape for the `OptionGroupsJson` multipart field on
 * POST /Service and PUT /Service/{id}. Matches Faris §6.1 exactly:
 * [{name,nameAr,selectionType,minSelect,maxSelect,kind,sortOrder,
 *   options:[{name,nameAr,price,currency,duration,sortOrder}]}]
 */
export interface OptionGroupWirePayload {
    name: string;
    nameAr: string | null;
    selectionType: OptionSelectionType;
    minSelect: number;
    maxSelect: number;
    kind: OptionGroupKind;
    sortOrder: number;
    options: Array<{
        name: string;
        nameAr: string | null;
        price: number;
        currency: string;
        /** "HH:mm:ss" */
        duration: string;
        sortOrder: number;
    }>;
}

/** Authoring caps (Faris §10.A — validator constraints, not DB constraints). */
export const MAX_GROUPS_PER_SERVICE = 8;
export const MAX_OPTIONS_PER_GROUP = 20;
